import { ChakraBox } from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import {
  NotificationAction,
  NotificationEventType,
  NotificationStatus,
  notificationActionToMaudPathMapper,
} from '~/lib/constants/notifications.constants';
import { getNotificationIdsToUpdate } from '~/lib/util/notification.util';
import { Path } from '~/routes/constants';

import NotificationCard from './NotificationCard';

const TOP_LEVEL_ID = 'notification-item';

interface NotificationItemProps {
  action?: string;
  channelName?: string;
  createdAt?: string;
  data?: any;
  dob?: string;
  eventType?: NotificationEventType;
  groupBy?: string;
  hoverWrapperBorder?: string;
  isRead?: boolean;
  newStatus?: string;
  notificationExternalId?: string;
  oldStatus?: string;
  onClose?: () => void;
  onUpdateNotification?: (
    notificationExternalIds: Array<string>,
    status: NotificationStatus,
  ) => void;
  patientName?: string;
  relatedNotifications: RelatedNotificationType[];
  subjectId?: string;
  type?: string;
  wrapperBackgroundColor?: string;
  wrapperBorder?: string;
}

const NotificationItem = ({
  action,
  channelName,
  createdAt,
  data,
  dob,
  eventType,
  groupBy,
  hoverWrapperBorder,
  isRead,
  newStatus,
  notificationExternalId,
  oldStatus,
  onClose,
  onUpdateNotification,
  patientName,
  relatedNotifications,
  subjectId,
  type,
  wrapperBackgroundColor = 'neutral.background.primary',
  wrapperBorder,
}: NotificationItemProps) => {
  const history = useHistory();

  const onRoute = (): void => {
    onClose?.();

    const path = notificationActionToMaudPathMapper[action];
    if (path) {
      let targetPath;
      switch (action) {
        case NotificationAction.OPEN_CHAT:
          targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/${path}?channelUrl=${groupBy}`;
          break;
        case NotificationAction.OPEN_PATIENT_NOTE:
          targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/${path}?noteType=Insurance card`;
          break;
        case NotificationAction.OPEN_PATIENT_PROFILE:
          targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/${path}`;
          if (eventType === NotificationEventType.UPDATED_ADMIN_EPISODE) {
            targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/care-planning`;
          } else {
            targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/${path}`;
          }
          break;
        case NotificationAction.OPEN_INQUIRY:
          // TODO: Normalize notification bodies that require OPEN_INQUIRY action. ASSIGN_INQUIRY and others are not consistent
          targetPath = `${Path.MAUD_INQUIRIES}?id=${data?.inquiryExternalId || data?.inquiryId}`;
          break;
        default:
          targetPath = `${Path.MAUD_PATIENTS}/${subjectId}/${path}`;
          break;
      }
      history.push(targetPath);
    }
  };

  const notificationClickHandler = (): void => {
    if (!isRead) {
      onUpdateNotification(
        [
          notificationExternalId,
          ...getNotificationIdsToUpdate(
            NotificationStatus.READ,
            relatedNotifications,
          ),
        ],
        NotificationStatus.READ,
      );
    }
    onRoute();
  };

  return (
    <ChakraBox
      _hover={{ border: hoverWrapperBorder }}
      backgroundColor={wrapperBackgroundColor}
      border={wrapperBorder}
      borderRadius="8px"
      cursor="pointer"
      data-group
      data-testid={`${TOP_LEVEL_ID}__${notificationExternalId}__wrapper`}
      id={`${TOP_LEVEL_ID}__${notificationExternalId}__wrapper`}
      marginBottom="8px"
      marginX="8px"
      onClick={notificationClickHandler}
      padding="8px"
    >
      <NotificationCard
        channelName={channelName}
        createdAt={createdAt}
        data={data}
        dob={dob}
        eventType={eventType}
        groupBy={groupBy}
        isRead={isRead}
        newStatus={newStatus}
        notificationExternalId={notificationExternalId}
        oldStatus={oldStatus}
        onUpdateNotification={onUpdateNotification}
        patientName={patientName}
        relatedNotifications={relatedNotifications}
        type={type}
      />
    </ChakraBox>
  );
};

export default NotificationItem;
